import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
  SecondaryButton,
  PrimaryButton,
  Input,
  Loader,
  SuccessModal,
  CountryDropdown,
} from 'common/components';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { ReactComponent as CloseIcon } from 'application/assets/close.svg';
import { ReactComponent as AddIcon } from 'application/assets/add.svg';
import { InternalUserAddModalProps } from './models';
import {
  StyledModal,
  Header,
  FormStyled,
  FieldsSection,
  ButtonSection,
  AddClinicButton,
} from './styles';
import { setSuccessCreated } from 'application/store/reducers/InternalUsers/InternalUsersSlice';
import { useCreateDoctorSchema } from '../../hooks/useCreateDoctorSchema';
import { AddProfessionalDataT } from '../../types/AddProfessionalDataT';
import Dropdown from 'common/components/Dropdown';
import { Specialization } from '@docbay/schemas';
import { useLocalizeKey } from 'common/hooks/useLocalizeKey';
import { AddProfessionalData } from '../../constants';
import ClinicForm from './ClinicForm';
import { v4 as uuid } from 'uuid';
import {
  getExternalProfessionalById,
  updateProfessionals,
} from 'application/store/reducers/ImportProfessionals/ActionCreators';
import { ExternalClinicResponseDto } from '@docbay/schemas/dist/external';
import Emails from '../Emails';
import compareObjectsChanges from 'common/helpers/compareObjectsChanges';
import Phones from '../Phones';
import { SupportedCountriesDto } from 'common/types/countries';

const ImportedDoctorEditModal: FC<InternalUserAddModalProps> = ({
  professionalId,
  opened,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [specializationIds, setSpecializationIds] = useState<string[]>([]);
  const [deletedClinicIds, setDeletedClinicId] = useState<string[]>([]);
  const [clinicsData, setClinicsData] = useState<ExternalClinicResponseDto[]>(
    [],
  );
  const { localizeNameKey } = useLocalizeKey();

  const createDoctorScheme = useCreateDoctorSchema();
  const { isLoading, successCreated, editedProfessional } = useAppSelector(
    (state) => state.importProfessionals,
  );
  const { specializations } = useAppSelector((state) => state.statistics);

  const specializationsOptions = useMemo(() => {
    const options = specializations.map((item) => {
      const specializationName = item[localizeNameKey as keyof Specialization];

      return {
        value: item.id,
        label: specializationName as string,
      };
    });
    return options;
  }, [specializations, i18n.language]);

  const {
    register,
    watch,
    reset,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    clearErrors,
    setError,
    control,
    formState: { errors },
  } = useForm<AddProfessionalDataT>({
    resolver: yupResolver(createDoctorScheme),
  });

  const formData = useWatch({ control });

  useEffect(() => {
    dispatch(getExternalProfessionalById(professionalId));
  }, [professionalId]);

  useEffect(() => {
    if (editedProfessional) {
      setClinicsData(editedProfessional.clinics!);

      const {
        firstName,
        lastName,
        primaryPhone,
        specializations,
        clinics,
        phones,
        supportedCountry,
      } = editedProfessional;

      specializations?.length && setSpecializationIds(specializationIds!);

      setValue('firstName', firstName!);
      setValue('lastName', lastName!);
      setValue('primaryPhone', primaryPhone!);
      setValue('phones', phones!);
      setValue('clinics', clinics);
      setValue('supportedCountry', {
        id: supportedCountry?.id || '',
        code: supportedCountry?.code || '',
        name: supportedCountry?.name || '',
      });
    }
  }, [editedProfessional]);

  const addNewClinic = () => {
    const newClinicData = [
      ...clinicsData,
      { ...AddProfessionalData, id: uuid() },
    ];
    setClinicsData(newClinicData);
  };

  const onSubmit = async () => {
    const { firstName, lastName, phones, emails, clinics, supportedCountry } =
      getValues();

    const formattedClinicsData = clinics.map((item, ind) => {
      const formattedPhones = item.phones?.map((phone) =>
        phone.includes('+') ? phone : `+${phone}`,
      );

      return {
        ...(clinicsData[ind]?.id?.includes('-')
          ? {}
          : { id: clinicsData[ind]?.id }),
        name: item.name,
        ...(item.phones?.length ? { phones: formattedPhones } : {}),
        ...(item?.emails?.length ? { emails: item.emails } : {}),
        ...(item.region ? { region: item.region } : {}),
        ...(item.city ? { city: item.city } : {}),
        ...(item.street ? { street: item.street } : {}),
        ...(item.latitude ? { latitude: item.latitude } : {}),
        ...(item.longitude ? { longitude: item.longitude } : {}),
        supportedCountryId: item.supportedCountry?.id,
      };
    });

    const professionalData = {
      firstName,
      lastName,
      specializationIds,
      primaryPhone: phones[0]?.includes('+') ? phones[0] : `+${phones[0]}`,
      ...(emails?.length ? { emails: emails as string[] } : {}),
      ...(specializationIds ? { specializationIds } : {}),
      clinics: formattedClinicsData,
      ...(deletedClinicIds.length
        ? { clinicIdsToRemove: deletedClinicIds }
        : {}),
      supportedCountryId: supportedCountry.id,
    };

    dispatch(
      updateProfessionals({
        id: editedProfessional?.id!,
        //@ts-ignore
        data: professionalData,
      }),
    ).then((data) => {
      if (data.meta.requestStatus === 'fulfilled') {
        reset();
        onClose();
      }
    });
  };

  const handleCloseModal = () => {
    reset();
    onClose();
    setSpecializationIds([]);
  };

  const handleCloseSuccessModal = () => {
    dispatch(setSuccessCreated(false));
    handleCloseModal();
  };

  const changeClinicData = (obj: ExternalClinicResponseDto, index: number) => {
    const newClinicData = [...clinicsData];
    newClinicData.splice(index, 1, obj);

    setClinicsData(newClinicData);
  };

  const deleteClinic = (id: string, index: number) => {
    if (!id.includes('-')) {
      setDeletedClinicId([...deletedClinicIds, id]);
    }
    const newClinicData = clinicsData.filter((item) => item.id !== id);
    const clinics = getValues('clinics');
    clinics.splice(index, 1);

    setValue('clinics', clinics);

    setClinicsData(newClinicData);
  };

  const isDataChanged = useMemo(() => {
    const { firstName, lastName, primaryPhone, emails, clinics } = getValues();

    const formattedClinicsData = clinics?.map((item, index) => {
      const formattedPhones = item.phones?.map((phone) =>
        phone.includes('+') ? phone : `+${phone}`,
      );

      return {
        ...(editedProfessional?.clinics[index]?.id
          ? {
              id: editedProfessional?.clinics[index].id,
            }
          : {}),
        name: item.name,
        latitude: item.latitude || null,
        longitude: item.longitude || null,
        city: item.city || null,
        street: item.street || null,
        region: item.region || null,
        phones: formattedPhones || null,
        emails: item.emails || [],
      };
    });

    const professionalData = {
      id: editedProfessional?.id || '',
      firstName,
      lastName,
      primaryPhone: primaryPhone,
      phones: editedProfessional?.phones?.length
        ? editedProfessional?.phones
        : null,
      ...(emails?.length ? { emails: emails as string[] } : {}),
      ...(specializationIds ? { specializations: specializationIds } : {}),
    };
    const professionalDataExisted = {
      id: editedProfessional?.id || '',
      firstName: editedProfessional?.firstName,
      lastName: editedProfessional?.lastName,
      primaryPhone: editedProfessional?.primaryPhone,
      phones: editedProfessional?.phones,
      emails: editedProfessional?.emails,
      specializations: editedProfessional?.specializations,
    };

    const hasChangesProfessional = compareObjectsChanges(
      professionalData,
      professionalDataExisted,
      true,
    );

    const hasChangesProfessionalClinics = compareObjectsChanges(
      formattedClinicsData,
      editedProfessional?.clinics,
    );

    return hasChangesProfessional || hasChangesProfessionalClinics;
  }, [formData, editedProfessional?.id]);

  return (
    <Modal open={opened}>
      <>
        {isLoading && <Loader />}
        {successCreated ? (
          <SuccessModal
            title={t('users_managment.doctor_create.success_modal_title') || ''}
            opened={successCreated}
            onClose={handleCloseSuccessModal}
          />
        ) : (
          <StyledModal>
            <Header>
              <h1>{t('users_managment.doctor_create.edit_user')}</h1>
              <button type="button" onClick={handleCloseModal}>
                <CloseIcon />
              </button>
            </Header>
            <FormStyled onSubmit={handleSubmit(onSubmit)}>
              <FieldsSection>
                <Input
                  id={'firstName'}
                  label={t('global.first_name') || ''}
                  placeholder={t(
                    'users_managment.doctor_create.first_name_placeholder',
                  )}
                  type="text"
                  register={register}
                  errorMessage={errors.firstName?.message}
                />
                <Input
                  id={'lastName'}
                  label={t('global.last_name') || ''}
                  placeholder={t(
                    'users_managment.doctor_create.last_name_placeholder',
                  )}
                  type="text"
                  register={register}
                  errorMessage={errors.lastName?.message}
                />
                <CountryDropdown
                  id="professional-country-dropdown"
                  label={t('country_of_practice')}
                  value={watch('supportedCountry')}
                  onChange={(country) =>
                    setValue(
                      'supportedCountry',
                      country as SupportedCountriesDto,
                      {
                        shouldValidate: !!errors.supportedCountry,
                      },
                    )
                  }
                />
                <Phones
                  errors={errors}
                  control={control}
                  register={register}
                  getValues={getValues}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  setError={setError}
                />
                <Emails
                  errors={errors}
                  control={control}
                  register={register}
                  getValues={getValues}
                  emails={editedProfessional?.emails || []}
                />
                <Dropdown
                  id={'specializationIds'}
                  value={specializationIds}
                  label={t('statistics.specializations')}
                  placeholder={t('statistics.specializations')}
                  onChange={(value) => {
                    setSpecializationIds(value as string[]);
                  }}
                  options={specializationsOptions}
                  withSearch={true}
                  isMulti={true}
                />
                <>
                  {clinicsData?.map((clinic, index) => {
                    return (
                      <ClinicForm
                        key={clinic.id}
                        clinic={clinic}
                        index={index}
                        register={register}
                        getValues={getValues}
                        watch={watch}
                        setValue={setValue}
                        trigger={trigger}
                        errors={errors}
                        control={control}
                        changeClinicData={changeClinicData}
                        showDeleteButton={clinicsData?.length > 1}
                        deleteClinic={deleteClinic}
                        clearErrors={clearErrors}
                        setError={setError}
                      />
                    );
                  })}
                </>
                <AddClinicButton onClick={addNewClinic}>
                  <AddIcon />
                  {t('users_managment.add_new_clinic')}
                </AddClinicButton>
              </FieldsSection>
              <ButtonSection>
                <SecondaryButton type="button" onClick={handleCloseModal}>
                  {t('global.cancel')}
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!!Object.keys(errors).length || !isDataChanged}
                >
                  {t('global.save')}
                </PrimaryButton>
              </ButtonSection>
            </FormStyled>
          </StyledModal>
        )}
      </>
    </Modal>
  );
};

export default ImportedDoctorEditModal;
